import { InitialPointOfImpact } from './initial-point-of-impact';
import { KeyValuePair } from './key-value-pair';
import { Person } from './person';

export const getInitialPointOfImpactDamageSelection = (
  damageSelections: DamageSelection[],
): DamageSelection | null => {
  if (damageSelections && damageSelections.length > 0) {
    const sortedDamgageSelections = damageSelections.sort(
      (a, b) => a.clickTime! - b.clickTime!,
    );
    const foundDamageSelection = sortedDamgageSelections[0];

    return foundDamageSelection;
  }

  return null;
};

export interface Violation {
  name: string;
  description: string;
}

export interface DamageSelection {
  pointOfImpactId: string;
  value?: boolean;
  isInitialPointOfImpact: boolean;
  clickTime?: number;
}

export interface Damage {
  additionalDamageDetails?: string;
  attachment?: string;
  attachmentDetails?: string;
  loadDetail?: number;
  loadSpillage?: number;
  damageSelection?: DamageSelection[]; // item 0 in the array is the initial point of impact, it will also be flagged as such
}

export interface Passenger extends Person {
  id: number;
  positionInVehicle: number | null;
  positionInVehicleDescription: string;
  safetyEquipmentUsed: number | null;
  safetyEquipmentUsedDescription?: string;
}

export interface Driver extends Person {
  licenceNumber?: string;
  licenceProvince?: string;
  positionInVehicleId?: number;
  safetyEquipmentUsed?: number;
  isLicenceValid?: boolean;
  isLicenceExpired?: boolean;
  isLicenceSuspended?: boolean;
}

export interface Insurance {
  insurancePolicyNumber: string;
  companyName: string;
  insurancePolicyExpirationDate: string;
}

export interface Vehicle {
  id?: number;
  vinNumber?: string;
  year?: string;
  make?: string;
  model?: string;
  type?: string;
  color?: string;
  licencePlateNumber?: string;
  province?: string;
  damage?: Damage;
  numberOfPassengers?: number | null;
  passengers?: Passenger[];
  driver?: Driver;
  isOwner?: boolean;
  owner?: Person;
  insuredBy?: Insurance;
  hasObtainedDamageEstimate?: boolean;
  damageEstimate?: number;
  isRoadConditionAFactor?: number | null;
  contributingRoadCondition?: number | null;
  isSurfaceConditionAFactor?: number | null;
  surfaceCondition?: number | null;
  isTrafficControlDeviceAFactor?: number | null;
  trafficControlDevice?: number | null;
  trafficControlCondition?: number | null;
  isVehicleConditionAFactor?: number | null;
  vehicleCondition?: number | null;
  isEnvironmentalConditionAFactor?: number | null;
  environmentalCondition?: number | null;
  isLightConditionAFactor?: number | null;
  lightConditionA?: number | null;
  lightConditionB?: number | null;
  isNoContributingFactors?: boolean;
  isPlateExpired?: boolean;
  isPlateLostOrStolen?: boolean;
  doesVehicleModelMatchWithMOVEs?: boolean;
  modelNameFromMOVEs?: string;
}

export interface Substep {
  //  substeps objects in future
}

export interface FormStep {
  step: number;
  name: string;
  nav: string;
  completed: boolean;
  inProgress: boolean;
  skipped: boolean;
  substeps: Substep[];
}

export class Metadata {
  formState: FormStep[];

  constructor(formState: FormStep[]) {
    this.formState = formState;
  }
}

export interface Collision {
  metadata?: Metadata;
  id?: number;
  wasHitAndRun?: boolean; //keep track of the type for eligibility questions ??
  isEligible?: boolean;
  eligibility: KeyValuePair<number, boolean | undefined>[];
  description?: string;
  primaryEvent?: number;
  objectIdentification?: number;
  animalType: number | null;
  severityOfCollision?: number;
  collisionLocation?: number;
  witnessOrCameraAvailable: boolean | null;
  collisionType?: number;
  collisionTypeFirstSaved?: boolean;
  location?: {
    useLocationServices?: boolean;
    locationString?: string;
    additionalLocationDetails?: string;
    latitude: number;
    longitude: number;
    zoom?: number;
  };
  vehicles?: Vehicle[];
  reportStatus?: number;
  reporterEmail?: string;
  reportedDate?: string;
  createdAt?: string;
  updatedAt?: string;
  dateOfCollision?: string;
  timeOfCollision?: string;
  dateUnknown?: boolean;
  timeUnknown?: boolean;
  directionOfTravel?: number;
  roadAlignmentA?: number;
  roadAlignmentB?: number;
  specialFacility?: number;
  travelingLane?: number;
  roadClass?: number;
  parkingLot?: boolean;
  additionalDetails?: string;
  caseNumber?: string;
  policeServiceCode?: string;
  invRegNumber?: string;
  approverRegNumber?: string;
  lawEnforcementAgencyCode?: string;
  lawEnforcementDistrictCode?: string;
  policeFileNumber?: string;
  reviewerRegNumber?: number;
  reviewerNotes?: string;
  ecollisionSubmissionDate?: string;
  completedDate?: string;
  agreementToTermsAndConditions?: boolean;
}

// generic handler for updating nested state object
export const handleNestedChange = <T>(
  path: string[],
  value: any,
  setState: React.Dispatch<React.SetStateAction<T>>,
) => {
  setState((prevState: T) => {
    // Start with a shallow copy of the state
    const newState = { ...prevState };
    let current: any = newState;

    // Traverse the path
    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];

      // Ensure the current level exists (or create it)
      if (current[key] === undefined) {
        current[key] = typeof path[i + 1] === 'number' ? [] : {};
      }

      // Move deeper into the object
      current[key] = { ...current[key] }; // Create a shallow copy at each level
      current = current[key];
    }

    // Set the value at the final key
    current[path[path.length - 1]] = value;

    return newState;
  });
};

export const defaultVehicle: Vehicle = {
  vinNumber: '', // Empty VIN
  year: '', // Empty manufacturing year
  make: '', // Empty vehicle make
  model: '', // Empty vehicle model
  type: '', // Empty vehicle type
  color: '', // Empty vehicle colour
  licencePlateNumber: '', // Empty license plate number
  damage: {
    additionalDamageDetails: '', // No additional damage details
    attachment: '', // No attachment
    attachmentDetails: '', // No attachment details
    loadDetail: undefined, // No load details
    loadSpillage: undefined, // No load spillage
    damageSelection: [], // No damage selections
  },
  passengers: [], // No passengers
  driver: {
    firstName: '', // Driver's first name
    middleName: '', // Driver's middle name (optional)
    lastName: '', // Driver's last name
    dateOfBirth: '', // Driver's date of birth (optional)
    gender: '', // Driver's gender
    address: {
      address: '', // Driver's address
      city: '', // Driver's city
      province: '', // Driver's province
      postalCode: '', // Driver's postal code
      country: '', // Driver's country
    },
    homePhoneNumber: '', // Driver's home phone number
    workPhoneNumber: '', // Driver's work phone number
    licenceNumber: '', // Driver's license number (optional)
    licenceProvince: '',
    positionInVehicleId: undefined, // Driver's position ID in vehicle
    safetyEquipmentUsed: undefined, // Default safety equipment ID
    isLicenceExpired: false,
    isLicenceSuspended: false,
  },
  isOwner: undefined, // Default: Driver is the owner
  owner: {
    firstName: '', // Owner's first name
    middleName: '', // Owner's middle name (optional)
    lastName: '', // Owner's last name
    dateOfBirth: '', // Owner's date of birth (optional)
    gender: '', // Owner's gender
    address: {
      address: '', // Owner's address
      city: '', // Owner's city
      province: '', // Owner's province
      postalCode: '', // Owner's postal code
      country: '', // Owner's country
    },
    homePhoneNumber: '', // Owner's home phone number
    workPhoneNumber: '', // Owner's work phone number
  },
  insuredBy: {
    insurancePolicyNumber: '', // Empty policy number
    companyName: '', // Empty insurance company name
    insurancePolicyExpirationDate: '', // Empty expiry date
  },
  damageEstimate: 0, // Default damage estimate
  isPlateExpired: false,
  isPlateLostOrStolen: false,
};

export function getFirstIncompleteStep(
  formState?: FormStep[],
  isHitAndRun?: boolean,
): FormStep | null {
  try {
    if (!formState) {
      return null;
    }

    if (!Array.isArray(formState)) {
      return null;
    }

    if (formState.length === 0) {
      return null;
    }

    const sortedFormState = [...formState].sort((a, b) => a.step - b.step);

    for (const step of sortedFormState) {
      if ((isHitAndRun && step.step === 7) || step.step === 9) {
        continue;
      }

      if (step.skipped === true || step.completed === false) {
        return step;
      }
    }

    return null;
  } catch (error) {
    return null;
  }
}

export function hasNavigatedSteps(formState?: FormStep[]): boolean {
  if (!formState || !Array.isArray(formState) || formState.length === 0) {
    return false;
  }

  return formState.some(
    (step) => step.skipped === true || step.completed === true,
  );
}
